import { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useLang, LANG_OPTIONS } from '../context/LanguageContext';
import { BUSINESS, businessPhoneDisplay, businessTelHref } from '../config/business';
import { useWhatsAppHelp } from '../context/WhatsAppHelpContext';
import BusinessLegalStrip from '../components/BusinessLegalStrip';
import './LoginPage.css';

import { API_URL as API } from '../config/env.js';
import { isNativeApp } from '../utils/platform.js';
import { signInWithGoogleNative } from '../utils/nativeGoogleAuth.js';

function getStoredReferral() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('ref');
    if (fromUrl) {
      localStorage.setItem('rk_ref', fromUrl.trim().toUpperCase());
      return fromUrl.trim().toUpperCase();
    }
    return localStorage.getItem('rk_ref') || '';
  } catch {
    return '';
  }
}

export default function LoginPage() {
  const { login } = useAuth();
  const { t, lang, switchLang } = useLang();
  const { openWhatsAppHelp } = useWhatsAppHelp();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralCode] = useState(() => getStoredReferral());

  useEffect(() => {
    getStoredReferral();
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    fetch('/api/ping')
      .then((res) => {
        if (!cancelled && !res.ok) {
          setError(t.signInErrorApiDev || 'API not running. In a second terminal run: npm run server');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t.signInErrorApiDev || 'API not running. In a second terminal run: npm run server');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const completeGoogleLogin = async (accessToken) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API || 'https://repatikosam.com'}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credential: accessToken,
          referralCode: referralCode || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t.signInErrorFailed || 'Google sign-in failed. Please try again.');
        return;
      }
      try {
        localStorage.removeItem('rk_ref');
      } catch {
        /* ignore */
      }
      login({
        email: data.email,
        name: data.name,
        token: data.token,
        isSubscribed: data.isSubscribed,
        isTrial: data.isTrial,
        isOwner: data.isOwner,
        isPreview: data.isPreview,
        isAdmin: data.isAdmin,
        subscribedUntil: data.subscribedUntil,
        trialUntil: data.trialUntil,
        referralCode: data.referralCode,
      });
      const next = new URLSearchParams(window.location.search).get('next') || '/';
      window.location.replace(next.startsWith('/') ? next : '/');
    } catch {
      setError(
        import.meta.env.DEV
          ? (t.signInErrorApiDev || 'API not running. In a second terminal run: npm run server')
          : (t.signInErrorNetwork || 'Cannot reach the server. Check your connection and try again.'),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      await completeGoogleLogin(tokenResponse.access_token);
    },
    onError: () => setError(t.signInErrorCancelled || 'Google sign-in was cancelled or blocked.'),
    flow: 'implicit',
  });

  const onSignInClick = async () => {
    if (loading) return;
    if (isNativeApp()) {
      setLoading(true);
      setError('');
      try {
        const tokenResponse = await signInWithGoogleNative();
        await completeGoogleLogin(tokenResponse.access_token);
      } catch (err) {
        setError(err?.message || t.signInErrorCancelled || 'Google sign-in was cancelled or blocked.');
        setLoading(false);
      }
      return;
    }
    handleGoogleLogin();
  };

  return (
    <div className="signin">
      <div className="signin__scene" aria-hidden>
        <div className="signin__mesh" />
        <div className="signin__columns">
          {Array.from({ length: 8 }, (_, i) => (
            <span key={i} className="signin__col-line" style={{ '--i': i }} />
          ))}
        </div>
        <div className="signin__float signin__float--1" />
        <div className="signin__float signin__float--2" />
      </div>

      <div className="signin__layout">
        <section className="signin__visual">
          <div className="signin__logo-orbit">
            <div className="signin__orbit signin__orbit--a" />
            <div className="signin__orbit signin__orbit--b" />
            <div className="signin__orbit signin__orbit--c" />
            <div className="signin__logo-core">
              <img src="/rk-logo.webp" alt={t.brandName || 'Repatikosam'} className="signin__logo-img" />
            </div>
          </div>
          <p className="signin__edition">{t.signInEdition}</p>
          <BusinessLegalStrip compact />
        </section>

        <section className="signin__panel">
          <div className="signin__panel-inner">
            <div className="signin__lang-bar">
              <p className="signin__lang-heading">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                {t.statLangLabel}
              </p>
              <div className="signin__lang-track" role="listbox" aria-label={t.statLangLabel}>
                {LANG_OPTIONS.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    role="option"
                    aria-selected={lang === l.code}
                    className={`signin__lang-option${lang === l.code ? ' is-active' : ''}`}
                    onClick={() => switchLang(l.code)}
                  >
                    <span className="signin__lang-option-badge">{l.short}</span>
                    <span className="signin__lang-option-name">{l.label}</span>
                    {lang === l.code && (
                      <svg className="signin__lang-option-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="signin__rule" aria-hidden />
            <p className="signin__label">{t.signInLabel}</p>
            <h1 className="signin__headline">
              {t.signInHeadline}
              <em> {t.signInHeadlineEm}</em>
            </h1>
            <p className="signin__copy">{t.signInCopy}</p>

            {error && (
              <p className="signin__error" role="alert">
                {error}
              </p>
            )}

            <button
              type="button"
              className="rk-google"
              onClick={onSignInClick}
              disabled={loading}
            >
              {loading ? (
                <span className="rk-google__spin" aria-hidden />
              ) : (
                <svg className="rk-google__icon" width="20" height="20" viewBox="0 0 24 24" aria-hidden>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              )}
              <span className="rk-google__label">{loading ? t.signInLoading : t.signInBtn}</span>
            </button>

            <div className="signin__meta">
              <div className="signin__meta-item">
                <strong>₹999</strong>
                <span>{t.signInMetaPerYear}</span>
              </div>
              <div className="signin__meta-div" />
              <div className="signin__meta-item">
                <strong>{t.signInMetaEpaper}</strong>
                <span>{t.signInMetaEpaperSub}</span>
              </div>
              <div className="signin__meta-div" />
              <div className="signin__meta-item">
                <strong>{t.signInMetaSecure}</strong>
                <span>{t.signInMetaSecureSub}</span>
              </div>
            </div>

            <p className="signin__legal">{t.signInLegal}</p>
            <div className="signin__contact">
              <a href={businessTelHref()}>{businessPhoneDisplay()}</a>
              <span aria-hidden> · </span>
              <button type="button" className="signin__wa-btn" onClick={() => openWhatsAppHelp('login')}>
                {t.contactWhatsAppLabel || t.navWhatsApp || 'WhatsApp'}
              </button>
              <span aria-hidden> · </span>
              <a href="/contact">{t.contact}</a>
            </div>
            <nav className="signin__policies" aria-label={t.legalPoliciesNav || 'Legal'}>
              <a href="/terms">{t.footerTerms}</a>
              <a href="/privacy">{t.footerPrivacy}</a>
              <a href="/refund">{t.footerRefund}</a>
              <a href="/cancellation">{t.footerCancellation}</a>
              <a href="/contact">{t.contact}</a>
            </nav>
          </div>

          <p className="signin__copy-foot">
            © {new Date().getFullYear()} {BUSINESS.legalName} · {t.brandName}
          </p>
        </section>
      </div>

      <div className="signin__ticker" aria-hidden>
        <div className="signin__ticker-track">
          <span>{t.signInTickerTruth}</span>
          <span>·</span>
          <span>{t.signInTickerEpaper}</span>
          <span>·</span>
          <span>{t.signInTickerEditions}</span>
          <span>·</span>
          <span>{t.signInTickerTruth}</span>
          <span>·</span>
          <span>{t.signInTickerEpaper}</span>
          <span>·</span>
          <span>{t.signInTickerEditions}</span>
          <span>·</span>
        </div>
      </div>
    </div>
  );
}
